import { useEffect, useState } from 'react'
import { Loader2, Search } from 'lucide-react'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import {
  useQualifierGoogleAdsSettings,
  useUpsertQualifierGoogleAdsSettings,
  useFetchConversionActions,
  type GoogleAdsDeliveryMethod,
  type FetchedConversionAction,
} from '@/hooks/useGHLQualifierDashboard'

interface FormState {
  deliveryMethod: GoogleAdsDeliveryMethod
  customerId: string
  conversionActionQualified: string
  conversionActionConverted: string
  conversionActionFormSubmit: string
  conversionActionPhoneCall: string
  testModeEnabled: boolean
  testButtonEnabled: boolean
  phoneCallOnly: boolean
}

const EMPTY_FORM: FormState = {
  deliveryMethod: 'ghl',
  customerId: '',
  conversionActionQualified: '',
  conversionActionConverted: '',
  conversionActionFormSubmit: '',
  conversionActionPhoneCall: '',
  testModeEnabled: false,
  testButtonEnabled: false,
  phoneCallOnly: false,
}

/** A fetched match that differs from the ID already saved in a field. */
interface FieldMismatch {
  old: string
  new: string
}

/** Per-field mismatch flags, keyed by the action-ID form field they apply to. */
type MismatchMap = Partial<
  Record<
    | 'conversionActionQualified'
    | 'conversionActionConverted'
    | 'conversionActionFormSubmit'
    | 'conversionActionPhoneCall',
    FieldMismatch
  >
>

interface QualifierGoogleAdsSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  locationId: string
  locationName: string | null
}

export function QualifierGoogleAdsSettingsDialog({
  open,
  onOpenChange,
  locationId,
  locationName,
}: QualifierGoogleAdsSettingsDialogProps) {
  const { toast } = useToast()
  const settingsQuery = useQualifierGoogleAdsSettings(locationId, open)
  const saveMutation = useUpsertQualifierGoogleAdsSettings()
  const fetchActions = useFetchConversionActions()

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  // Conversion actions fetched from Google Ads for the entered customer ID —
  // powers the per-field picker beneath each action-ID input. Reset whenever
  // the dialog reseeds so it never bleeds across locations.
  const [fetchedActions, setFetchedActions] = useState<FetchedConversionAction[]>([])
  // Per-field warnings raised when a fetched name match differs from the ID
  // already saved in that field. Recomputed on every fetch; cleared on reseed.
  const [mismatches, setMismatches] = useState<MismatchMap>({})

  // Seed local state from the server row when the dialog opens / data arrives.
  useEffect(() => {
    if (settingsQuery.data !== undefined) {
      setFetchedActions([])
      setMismatches({})
      const row = settingsQuery.data
      setForm(
        row
          ? {
              deliveryMethod: row.conversion_delivery_method,
              customerId: row.google_ads_customer_id ?? '',
              conversionActionQualified: row.google_ads_conversion_action_qualified ?? '',
              conversionActionConverted: row.google_ads_conversion_action_converted ?? '',
              conversionActionFormSubmit: row.google_ads_conversion_action_form_submit ?? '',
              conversionActionPhoneCall: row.google_ads_conversion_action_phone_call ?? '',
              testModeEnabled: row.test_mode_enabled ?? false,
              testButtonEnabled: row.test_button_enabled ?? false,
              phoneCallOnly: row.phone_call_only ?? false,
            }
          : EMPTY_FORM
      )
    }
  }, [settingsQuery.data])

  const isDirect = form.deliveryMethod === 'google_ads'
  // Phone-call-only locations need the customer ID + phone-call action ID +
  // converted action ID: on a confirmed booking their converted 8-day cycle
  // fires BOTH the phone_call and converted uploads (qualified stays
  // suppressed). A standard direct location requires customer ID + qualified +
  // converted.
  const directIncomplete =
    isDirect &&
    (form.phoneCallOnly
      ? form.customerId.trim() === '' ||
        form.conversionActionPhoneCall.trim() === '' ||
        form.conversionActionConverted.trim() === ''
      : form.customerId.trim() === '' ||
        form.conversionActionQualified.trim() === '' ||
        form.conversionActionConverted.trim() === '')

  const isLoading = settingsQuery.isLoading
  const isSaving = saveMutation.isPending
  const isFetching = fetchActions.isPending

  // Fetch the customer account's conversion actions, auto-fill the four fields
  // by name match, and keep the full list for the per-field pickers. Only
  // overwrites a field when a confident name match exists — never blanks a value
  // the operator already typed.
  const handleFetchActions = () => {
    const customerId = form.customerId.trim()
    if (!customerId) return

    fetchActions.mutate(
      { customerId },
      {
        onSuccess: (result) => {
          if (!result.ok) {
            toast({
              title:
                result.reason === 'not_configured'
                  ? 'Google Ads API not configured'
                  : 'Could not fetch conversion actions',
              description: result.error ?? 'Unknown error',
              variant: 'destructive',
            })
            return
          }

          const actions = result.actions ?? []
          const matched = result.matched
          setFetchedActions(actions)

          if (actions.length === 0) {
            toast({
              title: 'No conversion actions found',
              description: `Customer ${result.customerId} has no conversion actions, or the MCC can't access it.`,
              variant: 'destructive',
            })
            return
          }

          // Flag any slot where the fetched match differs from the ID already
          // saved in the field (old non-empty AND new !== old). Computed from the
          // pre-overwrite values captured here, before setForm replaces them.
          const flag = (
            oldVal: string,
            newVal: string | null | undefined
          ): FieldMismatch | undefined => {
            const o = oldVal.trim()
            return newVal && o && newVal !== o ? { old: o, new: newVal } : undefined
          }
          setMismatches({
            conversionActionQualified: flag(form.conversionActionQualified, matched?.qualified),
            conversionActionConverted: flag(form.conversionActionConverted, matched?.converted),
            conversionActionFormSubmit: flag(form.conversionActionFormSubmit, matched?.formSubmit),
            conversionActionPhoneCall: flag(form.conversionActionPhoneCall, matched?.phoneCall),
          })

          setForm((f) => ({
            ...f,
            conversionActionQualified: matched?.qualified ?? f.conversionActionQualified,
            conversionActionConverted: matched?.converted ?? f.conversionActionConverted,
            conversionActionFormSubmit: matched?.formSubmit ?? f.conversionActionFormSubmit,
            conversionActionPhoneCall: matched?.phoneCall ?? f.conversionActionPhoneCall,
          }))

          const matchCount = matched
            ? [matched.qualified, matched.converted, matched.formSubmit, matched.phoneCall].filter(
                Boolean
              ).length
            : 0
          toast({
            title: `Found ${actions.length} conversion action${actions.length === 1 ? '' : 's'}`,
            description:
              matchCount === 4
                ? 'All four fields auto-filled by name. Review and save.'
                : `Auto-filled ${matchCount} of 4 by name. Pick the rest from the dropdowns below.`,
          })
        },
        onError: (err: unknown) => {
          toast({
            title: 'Could not fetch conversion actions',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          })
        },
      }
    )
  }

  const handleSave = () => {
    saveMutation.mutate(
      {
        locationId,
        deliveryMethod: form.deliveryMethod,
        // Preserve the IDs regardless of method — the settings reader only
        // uses them when method === 'google_ads' anyway, and persisting them
        // means flipping back from 'ghl' restores the previously-typed values
        // instead of forcing the operator to re-enter them.
        customerId: form.customerId.trim() || null,
        conversionActionQualified: form.conversionActionQualified.trim() || null,
        conversionActionConverted: form.conversionActionConverted.trim() || null,
        conversionActionFormSubmit: form.conversionActionFormSubmit.trim() || null,
        conversionActionPhoneCall: form.conversionActionPhoneCall.trim() || null,
        testModeEnabled: form.testModeEnabled,
        testButtonEnabled: form.testButtonEnabled,
        phoneCallOnly: form.phoneCallOnly,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Google Ads settings saved',
            description: isDirect
              ? 'This location now uploads conversions directly to Google Ads. Disable its GHL → Google Ads workflow to avoid double counting.'
              : 'This location uses the standard GHL workflow for conversion uploads.',
          })
          onOpenChange(false)
        },
        onError: (err: unknown) => {
          toast({
            title: 'Could not save settings',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          })
        },
      }
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[540px]"
      >
        <SheetHeader className="space-y-1 border-b px-6 py-4 text-left">
          <SheetTitle>Google Ads Conversions — {locationName ?? locationId}</SheetTitle>
          <SheetDescription>
            Choose how this location&apos;s qualified and converted conversions reach Google Ads.
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="ga-direct" className="text-sm font-medium">
                  Direct Google Ads upload
                </Label>
                <p className="text-xs text-muted-foreground">
                  When off (default), the qualifier only applies tags and your GHL workflow uploads the
                  conversion. When on, the qualifier uploads directly via the Data Manager API — disable
                  this location&apos;s GHL → Google Ads workflow to avoid double counting.
                </p>
              </div>
              <Switch
                id="ga-direct"
                checked={isDirect}
                onCheckedChange={(v) =>
                  setForm((f) => ({ ...f, deliveryMethod: v ? 'google_ads' : 'ghl' }))
                }
                disabled={isSaving}
              />
            </div>

            {isDirect && (
              <div className="space-y-4 rounded-md border p-4">
                <FieldRow
                  id="ga-customer-id"
                  label="Google Ads Customer ID"
                  hint="Operating account that owns the conversion actions (digits only, no dashes)."
                  value={form.customerId}
                  onChange={(v) => setForm((f) => ({ ...f, customerId: v }))}
                  disabled={isSaving}
                />

                <div className="space-y-1">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={handleFetchActions}
                    disabled={isSaving || isFetching || form.customerId.trim() === ''}
                  >
                    {isFetching ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Fetching conversion actions...
                      </>
                    ) : (
                      <>
                        <Search className="mr-2 h-4 w-4" />
                        Fetch conversion IDs
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Reads this account&apos;s conversion actions from Google Ads and auto-fills the
                    fields below by name. Use the dropdowns to set any that don&apos;t match.
                  </p>
                </div>

                <div className="flex items-start justify-between gap-4 rounded-md bg-muted/40 p-3">
                  <div className="space-y-1">
                    <Label htmlFor="ga-phone-call-only" className="text-sm font-medium">
                      Phone calls only
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      For BookedRides accounts whose other conversions already flow through the
                      website&apos;s GTM. When <strong>on</strong>, the qualified pipeline is
                      suppressed. The <strong>phone-call</strong> conversion uploads ~1h after
                      contact creation, and the converted 8-day cycle runs ONLY for phone-call
                      contacts (the <code>new phone lead</code> tag), uploading the{' '}
                      <strong>converted</strong> conversion when a booking is confirmed (with the
                      normal GHL field + tag writes).
                    </p>
                  </div>
                  <Switch
                    id="ga-phone-call-only"
                    checked={form.phoneCallOnly}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, phoneCallOnly: v }))}
                    disabled={isSaving}
                  />
                </div>

                {!form.phoneCallOnly && (
                  <FieldRow
                    id="ga-action-qualified"
                    label="Qualified conversion action ID"
                    hint="Conversion action for qualified_lead."
                    value={form.conversionActionQualified}
                    onChange={(v) => setForm((f) => ({ ...f, conversionActionQualified: v }))}
                    disabled={isSaving}
                    options={fetchedActions}
                    mismatch={mismatches.conversionActionQualified}
                  />
                )}
                {/* Converted is required in BOTH modes: phone-call-only locations
                    run the converted 8-day cycle for their phone-call contacts and
                    upload the converted event when a booking is confirmed (the
                    phone-call conversion uploads separately, ~1h after contact
                    creation). */}
                <FieldRow
                  id="ga-action-converted"
                  label="Converted conversion action ID"
                  hint={
                    form.phoneCallOnly
                      ? 'Conversion action for converted_lead. Uploaded when a booking is confirmed (the phone-call conversion uploads separately, ~1h after contact creation).'
                      : 'Conversion action for converted_lead.'
                  }
                  value={form.conversionActionConverted}
                  onChange={(v) => setForm((f) => ({ ...f, conversionActionConverted: v }))}
                  disabled={isSaving}
                  options={fetchedActions}
                  mismatch={mismatches.conversionActionConverted}
                />
                {!form.phoneCallOnly && (
                  <FieldRow
                    id="ga-action-form-submit"
                    label="Form-submit conversion action ID (optional)"
                    hint="Conversion action for the form_submit event (new survey lead tag). Leave blank to skip this event."
                    value={form.conversionActionFormSubmit}
                    onChange={(v) => setForm((f) => ({ ...f, conversionActionFormSubmit: v }))}
                    disabled={isSaving}
                    options={fetchedActions}
                    mismatch={mismatches.conversionActionFormSubmit}
                  />
                )}
                <FieldRow
                  id="ga-action-phone-call"
                  label={
                    form.phoneCallOnly
                      ? 'Phone-call conversion action ID'
                      : 'Phone-call conversion action ID (optional)'
                  }
                  hint="Conversion action for the phone_call event (new phone lead tag). Leave blank to skip this event."
                  value={form.conversionActionPhoneCall}
                  onChange={(v) => setForm((f) => ({ ...f, conversionActionPhoneCall: v }))}
                  disabled={isSaving}
                  options={fetchedActions}
                  mismatch={mismatches.conversionActionPhoneCall}
                />
                {directIncomplete && (
                  <p className="text-xs text-destructive">
                    {form.phoneCallOnly
                      ? 'Customer ID and both the phone-call and converted conversion action IDs are required.'
                      : 'Customer ID and both conversion action IDs are required for direct upload.'}
                  </p>
                )}

                <div className="flex items-start justify-between gap-4 pt-2 border-t">
                  <div className="space-y-1">
                    <Label htmlFor="ga-test-mode" className="text-sm font-medium">
                      Test mode
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      When <strong>on</strong>: the cron applies the GHL tags only — it does{' '}
                      <strong>not</strong> auto-upload to Google Ads for this location. When{' '}
                      <strong>off</strong> (production): the cron applies tags <em>and</em>{' '}
                      uploads directly to Google Ads. Independent of the Test upload button below.
                    </p>
                  </div>
                  <Switch
                    id="ga-test-mode"
                    checked={form.testModeEnabled}
                    onCheckedChange={(v) =>
                      setForm((f) => ({ ...f, testModeEnabled: v }))
                    }
                    disabled={isSaving}
                  />
                </div>

                <div className="flex items-start justify-between gap-4 pt-2">
                  <div className="space-y-1">
                    <Label htmlFor="ga-test-button" className="text-sm font-medium">
                      Show Test upload button
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      When <strong>on</strong>: a per-row <strong>Test upload</strong> button
                      appears on this location&apos;s Qualified and Converted leads tables,
                      letting you fire a conversion manually. The test endpoint also accepts calls
                      for this location. Independent of Test mode.
                    </p>
                  </div>
                  <Switch
                    id="ga-test-button"
                    checked={form.testButtonEnabled}
                    onCheckedChange={(v) =>
                      setForm((f) => ({ ...f, testButtonEnabled: v }))
                    }
                    disabled={isSaving}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <SheetFooter className="border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || isSaving || directIncomplete}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

interface FieldRowProps {
  id: string
  label: string
  hint: string
  value: string
  onChange: (v: string) => void
  disabled: boolean
  /**
   * Conversion actions fetched from Google Ads. When non-empty, a picker
   * renders beneath the input so the operator can choose an action by name
   * instead of pasting an ID — the fallback for anything name-matching missed.
   */
  options?: FetchedConversionAction[]
  /**
   * Set when the last fetch replaced a previously-saved ID with a different
   * name match. Shows an inline warning (old → new) while the fetched value is
   * still in the field, with a one-click Revert back to the saved ID.
   */
  mismatch?: FieldMismatch
}

function FieldRow({ id, label, hint, value, onChange, disabled, options, mismatch }: FieldRowProps) {
  const hasOptions = !!options && options.length > 0
  // Only reflect the current value in the picker when it's one of the fetched
  // actions; a manually-typed ID that isn't in the list leaves the picker
  // unselected rather than showing a misleading placeholder match.
  const selectedValue = hasOptions && options!.some((o) => o.id === value) ? value : undefined
  // Only warn while the fetched value is still in place. Once the operator
  // edits the field, picks from the dropdown, or reverts, the warning clears.
  const showMismatch = !!mismatch && value === mismatch.new

  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
      </Label>
      <Input
        id={id}
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="e.g. 1234567890"
      />
      {showMismatch && (
        <p className="text-xs text-destructive">
          Doesn&apos;t match your saved ID — was{' '}
          <span className="font-medium">{mismatch!.old}</span>, fetched{' '}
          <span className="font-medium">{mismatch!.new}</span>.{' '}
          <button
            type="button"
            className="font-medium underline underline-offset-2 disabled:opacity-50"
            onClick={() => onChange(mismatch!.old)}
            disabled={disabled}
          >
            Revert
          </button>
        </p>
      )}
      {hasOptions && (
        <Select value={selectedValue} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger className="h-8 text-xs" aria-label={`${label} — choose from fetched actions`}>
            <SelectValue placeholder="Or choose a fetched conversion action…" />
          </SelectTrigger>
          <SelectContent>
            {options!.map((action) => (
              <SelectItem key={action.id} value={action.id} className="text-xs">
                {action.name || '(unnamed)'}
                {action.status && action.status !== 'ENABLED' ? ` · ${action.status}` : ''} · {action.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  )
}
